<template>
  <div class="analyze_section">
    <div class="analyze_section_header">
      <h3>Analiza rezultatov (LLM)</h3>
      <select :value="model" class="model_select" @change="setModel($event.target.value)">
        <option value="gpt-5-mini" title="Velik kontekst, zmogljivo razumevanje, razmišljanje, 0.25 USD/M tokenov">
          gpt-5-mini</option>
        <option value="gpt-5-nano" title="Velik kontekst, srednje razumevanje, razmišljanje, 0.05 USD/M tokenov">gpt-5-nano</option>
        <option value="gpt-4o-mini" title="Majhen kontekst, srednje razumevanje, zelo hiter, 0.15 USD/M tokenov">
          gpt-4o-mini</option>
      </select>
    </div>

    <div class="analyze_chat_history" ref="analyzeHistoryEl">
      <div v-for="(item, idx) in analyzeHistory" :key="idx" class="analyze_history_item">
        <div class="analyze_history_item_header">
          <div class="analyze_history_instruction">{{ cleanAnalyzeInstruction(item.instruction) }}</div>
          <button type="button" class="analyze_copy_btn" title="Kopiraj (HTML)" @click="copyAnalyzeItem(item)"
            aria-label="Kopiraj vprašanje in odgovor">
            <font-awesome-icon :icon="['far', 'copy']" />
          </button>
        </div>
        <div class="analyze_history_result_html" v-html="cleanAnalyzeResultHtml(item.result)"></div>
        <div v-if="item.usage" class="analyze_usage_small">
          Tokeni: vhod {{ item.usage.prompt_tokens.toLocaleString() }},
          odgovor {{ item.usage.completion_tokens.toLocaleString() }},
          skupaj {{ item.usage.total_tokens.toLocaleString() }}{{ formatItemCost(item) }}
        </div>
      </div>
    </div>

    <div class="analyze_chat_input">
      <div class="form_row">
        <div v-if="analyzeError" class="result_error">{{ analyzeError }}</div>
        <label>Navodilo</label>
        <textarea :value="instruction" rows="2" placeholder="Naredi povzetek / naredi tabelo / primerjaj …"
          @input="setInstruction($event.target.value)"></textarea>
      </div>
      <div class="form_actions">
        <button type="button" class="button button_with_loading"
          :class="{ disabled: !lastSearchData || !instruction.trim() || analyzeLoading }"
          @click="runAnalyze"
          :disabled="!lastSearchData || !instruction.trim() || analyzeLoading">
          <font-awesome-icon v-if="analyzeLoading" :icon="['fas', 'spinner']" spin class="btn_loading_icon"
            aria-hidden="true" />
          <span>{{ analyzeLoading ? 'Analiziram…' : 'Analiziraj rezultate' }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    model: { type: String, default: 'gpt-5-nano' },
  },
  emits: ['update:model'],
  data() {
    return {
      instruction: ''
    };
  },
  watch: {
    analyzeHistory: {
      deep: false,
      handler() {
        this.$nextTick(() => {
          const el = this.$refs.analyzeHistoryEl;
          if (el) el.scrollTop = el.scrollHeight;
        });
      }
    }
  },
  computed: {
    analyzeHistory() {
      return this.$store.getters.evalvation_analyze_history || [];
    },
    analyzeLoading() {
      return this.$store.getters.evalvation_analyze_loading === true;
    },
    analyzeError() {
      return this.$store.getters.evalvation_analyze_error;
    },
    lastSearchData() {
      return this.$store.getters.evalvation_search_data;
    },
    modelPrices() {
      // kept local to Analyze panel, to avoid prop plumbing
      return {
        'gpt-5-nano': { input: 0.05, output: 0.4 },
        'gpt-5-mini': { input: 0.25, output: 2.0 },
        'gpt-4o-mini': { input: 0.15, output: 0.6 }
      };
    }
  },
  methods: {
    cleanAnalyzeInstruction(text) {
      if (text == null) return '';
      return String(text).replace(/\n{2,}/g, '\n').trim();
    },
    cleanAnalyzeResultHtml(html) {
      if (html == null || !String(html).trim()) return '';
      const div = document.createElement('div');
      div.innerHTML = String(html).trim();

      const removeWhitespaceTextNodes = (node) => {
        const toRemove = [];
        node.childNodes.forEach((child) => {
          if (child.nodeType === Node.TEXT_NODE && /^\s*$/.test(child.textContent)) {
            toRemove.push(child);
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            removeWhitespaceTextNodes(child);
          }
        });
        toRemove.forEach((n) => n.remove());
      };

      removeWhitespaceTextNodes(div);

      const emptySelectors = 'p, div, span, li, td, th, h1, h2, h3, h4, h5, h6';
      let removed;
      do {
        removed = 0;
        div.querySelectorAll(emptySelectors).forEach((el) => {
          const text = (el.textContent || '').trim();
          if (!text) {
            el.remove();
            removed++;
          }
        });
      } while (removed > 0);

      removeWhitespaceTextNodes(div);

      return div.innerHTML
        .replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '<br>')
        .replace(/\n{2,}/g, '\n')
        .replace(/>\s*\n\s*</g, '><');
    },
    costForModel(model, promptTokens, completionTokens) {
      const prices = this.modelPrices && this.modelPrices[model];
      if (!prices || promptTokens == null || completionTokens == null) return null;
      const inputCost = (promptTokens / 1e6) * prices.input;
      const outputCost = (completionTokens / 1e6) * prices.output;
      return { inputCost, outputCost, total: inputCost + outputCost };
    },
    formatItemCost(item) {
      if (!item || !item.usage) return '';
      const c = this.costForModel(item.model || 'gpt-5-mini', item.usage.prompt_tokens, item.usage.completion_tokens);
      if (!c) return '';
      return ` · Cena: $${c.total.toFixed(4)} (vhod $${c.inputCost.toFixed(4)}, odgovor $${c.outputCost.toFixed(4)})`;
    },
    async copyAnalyzeItem(item) {
      const question = (item && item.instruction != null ? String(item.instruction) : '').trim();
      const resultHtml = (item && item.result != null ? String(item.result) : '').trim();
      let answerPlain = '';
      if (resultHtml) {
        const div = document.createElement('div');
        div.innerHTML = resultHtml;
        answerPlain = (div.textContent || div.innerText || '').trim();
      }
      const plainCombined = question && answerPlain
        ? `${question}\n\n${answerPlain}`
        : question || answerPlain;
      if (!plainCombined) return;

      try {
        const escapeHtml = (s) => String(s)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
        const htmlBlob = new Blob(
          [`<p><strong>Vprašanje:</strong> ${escapeHtml(question)}</p>\n${resultHtml || ''}`.trim()],
          { type: 'text/html' }
        );
        const textBlob = new Blob([plainCombined], { type: 'text/plain' });
        await navigator.clipboard.write([
          new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob })
        ]);
      } catch (e) {
        try {
          await navigator.clipboard.writeText(plainCombined);
        } catch (e2) {
          console.warn('Copy failed', e2);
        }
      }
    },
    setInstruction(v) {
      this.instruction = v;
    },
    setModel(v) {
      this.$emit('update:model', v);
    },
    runAnalyze() {
      if (!this.lastSearchData || !this.instruction.trim()) return;
      const instruction = this.instruction.trim();
      this.$store.dispatch('evalvationAiAnalyze', {
        instruction,
        model: this.model || 'gpt-5-mini',
        searchResponse: this.lastSearchData
      });
    }
  }
};
</script>

